// MeasurementsForm — agent edits the player_measurements satellite row
// for a player they own.
//
// M5.3. Pairs with PUT /api/players/:id/measurements. Renders as its
// own form alongside PlayerForm in PlayerEdit — the columns live in a
// separate D1 table so the write goes to its own endpoint, and the
// form's "all fields optional, empty = no change / clear" UX is its
// own concern.

import { useEffect, useState, type FormEvent } from 'react'
import { apiFetch } from '../../lib/api'

export interface MeasurementsValues {
  height_cm: string
  weight_kg: string
  wingspan_cm: string
  standing_reach_cm: string
  max_vert_cm: string
  measured_at: string
  measured_by: string
}

export const emptyMeasurements: MeasurementsValues = {
  height_cm: '',
  weight_kg: '',
  wingspan_cm: '',
  standing_reach_cm: '',
  max_vert_cm: '',
  measured_at: '',
  measured_by: '',
}

interface Props {
  playerId: string
  // Initial values fetched from /api/profile/:slug (the public read
  // already surfaces the measurements block, so we hydrate from there
  // without a second round-trip).
  initial: MeasurementsValues
}

export default function MeasurementsForm({ playerId, initial }: Props) {
  const [form, setForm] = useState<MeasurementsValues>(initial)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedOk, setSavedOk] = useState(false)

  // Reset form when the playerId changes (the parent might switch
  // players while staying on the same route via a re-mount).
  useEffect(() => {
    setForm(initial)
    setSavedOk(false)
    setErr(null)
  }, [playerId, initial])

  function update<K extends keyof MeasurementsValues>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setErr(null)
    setSavedOk(false)

    // Build the patch. Empty inputs map to `null` (clear the column),
    // numeric strings parse to numbers, malformed inputs short-circuit
    // here so the server doesn't have to.
    const body: Record<string, number | string | null> = {}

    function pushNumeric(key: keyof MeasurementsValues): string | null {
      const raw = form[key].trim()
      if (raw === '') {
        body[key] = null
        return null
      }
      const n = Number(raw)
      if (!Number.isFinite(n)) return `${key} must be a number`
      body[key] = n
      return null
    }
    for (const k of [
      'height_cm',
      'weight_kg',
      'wingspan_cm',
      'standing_reach_cm',
      'max_vert_cm',
    ] as const) {
      const msg = pushNumeric(k)
      if (msg) {
        setErr(msg)
        return
      }
    }
    body.measured_at = form.measured_at.trim() === '' ? null : form.measured_at.trim()
    body.measured_by = form.measured_by.trim() === '' ? null : form.measured_by.trim()

    setSubmitting(true)
    try {
      const res = await apiFetch(
        `/api/players/${encodeURIComponent(playerId)}/measurements`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        const key = body?.error
        const friendly =
          key === 'height_cm_out_of_range'
            ? 'Height looks off — sanity range is 100–250 cm.'
            : key === 'weight_kg_out_of_range'
              ? 'Weight looks off — sanity range is 30–200 kg.'
              : key === 'wingspan_cm_out_of_range'
                ? 'Wingspan looks off — sanity range is 100–280 cm.'
                : key === 'standing_reach_cm_out_of_range'
                  ? 'Standing reach looks off — sanity range is 150–350 cm.'
                  : key === 'max_vert_cm_out_of_range'
                    ? 'Vertical jump looks off — sanity range is 0–150 cm.'
                    : key === 'measured_at_invalid'
                      ? 'Measured-at must be YYYY-MM-DD.'
                      : key === 'empty_patch'
                        ? 'Nothing to save — at least one field must be filled.'
                        : key ?? `Request failed (${res.status})`
        setErr(friendly)
        return
      }
      setSavedOk(true)
      window.setTimeout(() => setSavedOk(false), 2500)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'network_error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={submit}
      className="mt-8 rounded-lg border border-hoop-border bg-hoop-bg p-5"
    >
      <h2 className="text-sm font-semibold text-hoop-text mb-1">
        Physical measurements
      </h2>
      {/* C3 / QA N2 — measurements POST to their own endpoint, so they save
          independently of the "Save changes" button above. Call that out so a
          user editing here doesn't assume the main Save will persist them. */}
      <p className="text-xs text-hoop-text-3 mb-4">
        All fields optional. Leave blank to clear. Numbers only. This section
        saves on its own — use the Save measurements button below.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Height (cm)" value={form.height_cm} onChange={(v) => update('height_cm', v)} />
        <NumberField label="Weight (kg)" value={form.weight_kg} onChange={(v) => update('weight_kg', v)} />
        <NumberField label="Wingspan (cm)" value={form.wingspan_cm} onChange={(v) => update('wingspan_cm', v)} />
        <NumberField label="Standing reach (cm)" value={form.standing_reach_cm} onChange={(v) => update('standing_reach_cm', v)} />
        <NumberField label="Vertical jump (cm)" value={form.max_vert_cm} onChange={(v) => update('max_vert_cm', v)} />
        <TextField label="Measured on" type="date" value={form.measured_at} onChange={(v) => update('measured_at', v)} />
      </div>
      <div className="mt-3">
        <TextField label="Measured by" value={form.measured_by} onChange={(v) => update('measured_by', v)} placeholder="Combine staff, agent, etc." />
      </div>

      {err && (
        <p className="mt-3 text-sm text-hoop-red">{err}</p>
      )}
      {savedOk && (
        <p className="mt-3 text-sm text-hoop-green">
          Measurements saved.
        </p>
      )}

      <div className="mt-4">
        <button
          type="submit"
          disabled={submitting}
          className="bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
        >
          {submitting ? 'Saving…' : 'Save measurements'}
        </button>
      </div>
    </form>
  )
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="text-xs text-hoop-text-2">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-hoop-border bg-hoop-surface px-2 py-1.5 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
      />
    </label>
  )
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  // 'date' renders the native calendar picker; its value round-trips as
  // YYYY-MM-DD, which is exactly what the measurements endpoint expects.
  type?: string
}) {
  return (
    <label className="block">
      <span className="text-xs text-hoop-text-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-hoop-border bg-hoop-surface px-2 py-1.5 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
      />
    </label>
  )
}
